import { Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { EngineeringNav } from '@/components/EngineeringNav';
import { ArrowRight, Flame, PenTool, Scan, Ruler, CheckCircle, Box, Wrench } from 'lucide-react';

export default function EngineeringServices() {
  const location = useLocation();

  // Handle hash navigation - scroll to element when navigating from another page
  useEffect(() => {
    if (location.hash) {
      // Small delay to ensure the page has rendered
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          const navHeight = 100; // Account for fixed nav
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({
            top: elementPosition - navHeight,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-black" data-testid="engineering-services">
      <EngineeringNav />
      
      {/* Hero */}
      <section className="bg-[#0a0a0a] py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="font-nunito text-sm text-[#F54021] mb-4 tracking-wider uppercase font-bold">Our Services</p>
            <h1 className="font-nunito text-4xl lg:text-5xl font-extrabold text-white mb-6">
              Engineering Excellence, Delivered
            </h1>
            <p className="font-nunito text-lg text-gray-400">
              From initial concept to final production, we offer comprehensive engineering services 
              to bring your projects to life with precision and quality.
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24">
          
          {/* CAD Design */}
          <div id="cad" className="grid lg:grid-cols-2 gap-12 items-center scroll-mt-32" data-testid="service-cad">
            <div>
              <div className="w-14 h-14 rounded-full bg-[#F54021]/20 border border-[#F54021] flex items-center justify-center mb-6">
                <PenTool className="w-7 h-7 text-[#F54021]" />
              </div>
              <h2 className="font-nunito text-3xl font-bold text-white mb-4">CAD Design</h2>
              <p className="font-nunito text-gray-400 mb-6">
                Professional CAD design services using industry-standard software.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 font-nunito text-gray-300">
                  <CheckCircle className="w-5 h-5 text-[#F54021]" />Solid Edge, Onshape
                </li>
                <li className="flex items-center gap-3 font-nunito text-gray-300">
                  <CheckCircle className="w-5 h-5 text-[#F54021]" />2D drawings & 3D models
                </li>
                <li className="flex items-center gap-3 font-nunito text-gray-300">
                  <CheckCircle className="w-5 h-5 text-[#F54021]" />Design for manufacturing
                </li>
              </ul>
              <Link to="/engineering/quote" className="inline-flex items-center gap-2 text-[#F54021] font-nunito font-semibold hover:gap-3 transition-all">
                Request a quote <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="rounded-2xl overflow-hidden border border-[#333333]">
              <img src="https://images.unsplash.com/photo-1580982330720-bd5e0fed108b?w=600" alt="CAD Design" className="w-full h-80 object-cover" />
            </div>
          </div>

          {/* Technical Drawing */}
          <div id="drawing" className="grid lg:grid-cols-2 gap-12 items-center scroll-mt-32" data-testid="service-drawing">
            <div className="lg:order-2">
              <div className="w-14 h-14 rounded-full bg-[#F54021]/20 border border-[#F54021] flex items-center justify-center mb-6">
                <Ruler className="w-7 h-7 text-[#F54021]" />
              </div>
              <h2 className="font-nunito text-3xl font-bold text-white mb-4">Technical Drawing</h2>
              <p className="font-nunito text-gray-400 mb-6">
                Detailed engineering drawings and documentation for manufacturing.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 font-nunito text-gray-300">
                  <CheckCircle className="w-5 h-5 text-[#F54021]" />Manufacturing drawings
                </li>
                <li className="flex items-center gap-3 font-nunito text-gray-300">
                  <CheckCircle className="w-5 h-5 text-[#F54021]" />Assembly instructions
                </li>
                <li className="flex items-center gap-3 font-nunito text-gray-300">
                  <CheckCircle className="w-5 h-5 text-[#F54021]" />GD&T tolerancing
                </li>
              </ul>
              <Link to="/engineering/quote" className="inline-flex items-center gap-2 text-[#F54021] font-nunito font-semibold hover:gap-3 transition-all">
                Request a quote <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="lg:order-1 rounded-2xl overflow-hidden border border-[#333333] bg-white">
              <img src="https://customer-assets.emergentagent.com/job_dual-portfolio-4/artifacts/j0yw5p3c_image.png" alt="Technical Drawing" className="w-full h-80 object-contain p-4" />
            </div>
          </div>

          {/* 3D Scanning */}
          <div id="scanning" className="grid lg:grid-cols-2 gap-12 items-center scroll-mt-32" data-testid="service-scanning">
            <div>
              <div className="w-14 h-14 rounded-full bg-[#F54021]/20 border border-[#F54021] flex items-center justify-center mb-6">
                <Scan className="w-7 h-7 text-[#F54021]" />
              </div>
              <h2 className="font-nunito text-3xl font-bold text-white mb-4">3D Scanning</h2>
              <p className="font-nunito text-gray-400 mb-6">
                Accurate digital capture of physical objects for reverse engineering and quality control.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 font-nunito text-gray-300">
                  <CheckCircle className="w-5 h-5 text-[#F54021]" />High-resolution scanning
                </li>
                <li className="flex items-center gap-3 font-nunito text-gray-300">
                  <CheckCircle className="w-5 h-5 text-[#F54021]" />Point cloud data
                </li>
                <li className="flex items-center gap-3 font-nunito text-gray-300">
                  <CheckCircle className="w-5 h-5 text-[#F54021]" />Quality inspection reports
                </li>
              </ul>
              <Link to="/engineering/quote" className="inline-flex items-center gap-2 text-[#F54021] font-nunito font-semibold hover:gap-3 transition-all">
                Request a quote <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="rounded-2xl overflow-hidden border border-[#333333]">
              <img src="https://customer-assets.emergentagent.com/job_cad-craft-studio-1/artifacts/a3jb0rbj_image.png" alt="3D Scanning" className="w-full h-80 object-cover" />
            </div>
          </div>

          {/* CNC Plasma Cutting */}
          <div id="plasma" className="grid lg:grid-cols-2 gap-12 items-center scroll-mt-32" data-testid="service-plasma">
            <div className="lg:order-2">
              <div className="w-14 h-14 rounded-full bg-[#F54021]/20 border border-[#F54021] flex items-center justify-center mb-6">
                <Flame className="w-7 h-7 text-[#F54021]" />
              </div>
              <h2 className="font-nunito text-3xl font-bold text-white mb-4">CNC Plasma Cutting</h2>
              <p className="font-nunito text-gray-400 mb-6">
                High-precision plasma cutting for metals up to 20mm thick. Perfect for industrial parts, decorative panels, and custom designs.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 font-nunito text-gray-300">
                  <CheckCircle className="w-5 h-5 text-[#F54021]" />Steel, aluminum, stainless steel
                </li>
                <li className="flex items-center gap-3 font-nunito text-gray-300">
                  <CheckCircle className="w-5 h-5 text-[#F54021]" />Complex shapes & intricate patterns
                </li>
                <li className="flex items-center gap-3 font-nunito text-gray-300">
                  <CheckCircle className="w-5 h-5 text-[#F54021]" />Batch production available
                </li>
              </ul>
              <Link to="/engineering/quote" className="inline-flex items-center gap-2 text-[#F54021] font-nunito font-semibold hover:gap-3 transition-all">
                Request a quote <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="lg:order-1 rounded-2xl overflow-hidden border border-[#333333]">
              <img src="https://customer-assets.emergentagent.com/job_cad-craft-studio-1/artifacts/bzzqku93_image.png" alt="CNC Plasma Cutting" className="w-full h-80 object-cover" />
            </div>
          </div>

          {/* 3D Printing */}
          <div id="printing" className="grid lg:grid-cols-2 gap-12 items-center scroll-mt-32" data-testid="service-printing">
            <div>
              <div className="w-14 h-14 rounded-full bg-[#F54021]/20 border border-[#F54021] flex items-center justify-center mb-6">
                <Box className="w-7 h-7 text-[#F54021]" />
              </div>
              <h2 className="font-nunito text-3xl font-bold text-white mb-4">3D Printing</h2>
              <p className="font-nunito text-gray-400 mb-6">
                Rapid prototyping and custom part production using additive manufacturing technology.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 font-nunito text-gray-300">
                  <CheckCircle className="w-5 h-5 text-[#F54021]" />FDM & resin printing
                </li>
                <li className="flex items-center gap-3 font-nunito text-gray-300">
                  <CheckCircle className="w-5 h-5 text-[#F54021]" />Rapid prototyping
                </li>
                <li className="flex items-center gap-3 font-nunito text-gray-300">
                  <CheckCircle className="w-5 h-5 text-[#F54021]" />Custom jigs & fixtures
                </li>
              </ul>
              <Link to="/engineering/quote" className="inline-flex items-center gap-2 text-[#F54021] font-nunito font-semibold hover:gap-3 transition-all">
                Request a quote <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="rounded-2xl overflow-hidden border border-[#333333]">
              <img src="https://customer-assets.emergentagent.com/job_cad-craft-studio-1/artifacts/z7p1ds8n_image.png" alt="3D Printing" className="w-full h-80 object-cover" />
            </div>
          </div>

          {/* Fabrication */}
          <div id="fabrication" className="grid lg:grid-cols-2 gap-12 items-center scroll-mt-32" data-testid="service-fabrication">
            <div className="lg:order-2">
              <div className="w-14 h-14 rounded-full bg-[#F54021]/20 border border-[#F54021] flex items-center justify-center mb-6">
                <Wrench className="w-7 h-7 text-[#F54021]" />
              </div>
              <h2 className="font-nunito text-3xl font-bold text-white mb-4">Fabrication</h2>
              <p className="font-nunito text-gray-400 mb-6">
                Custom metal fabrication and assembly services for one-off projects and small batch production.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 font-nunito text-gray-300">
                  <CheckCircle className="w-5 h-5 text-[#F54021]" />MIG & TIG welding
                </li>
                <li className="flex items-center gap-3 font-nunito text-gray-300">
                  <CheckCircle className="w-5 h-5 text-[#F54021]" />Bending & forming
                </li>
                <li className="flex items-center gap-3 font-nunito text-gray-300">
                  <CheckCircle className="w-5 h-5 text-[#F54021]" />Assembly & finishing
                </li>
              </ul>
              <Link to="/engineering/quote" className="inline-flex items-center gap-2 text-[#F54021] font-nunito font-semibold hover:gap-3 transition-all">
                Request a quote <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="lg:order-1 rounded-2xl overflow-hidden border border-[#333333]">
              <img src="https://images.unsplash.com/photo-1738162837408-5fbf53f0b97a?w=600" alt="Fabrication" className="w-full h-80 object-cover" />
            </div>
          </div>

        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#F54021] to-[#c73018]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-nunito text-3xl lg:text-4xl font-bold text-white mb-4">
            Need a Custom Solution?
          </h2>
          <p className="font-nunito text-white/90 mb-8 max-w-2xl mx-auto">
            We specialize in tackling unique engineering challenges. Let's discuss your project.
          </p>
          <Link 
            to="/engineering/quote"
            className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white font-nunito font-bold rounded-full hover:bg-[#1a1a1a] transition-colors"
            data-testid="get-quote-services-btn"
          >
            Get Started Today <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-[#333333] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-nunito text-sm text-gray-500">© 2024 BJ Engineering. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
